import React, { useState } from "react";
import { Surface } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Download, Loader2, Cable, Database, Zap, Calendar, ShieldCheck, KeyRound } from "lucide-react";
import { API, getToken } from "@/lib/api";
import { toast } from "sonner";

const BASE_URL = "https://wingsapiservices.wingsoncloud.co.in/TharaAPIs/get_data";

export default function ApiDocs() {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = getToken();
      const url = `${API}/wings-api-spec.pdf?auth=${encodeURIComponent(token)}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = "thara-gold-wings-sync-api-spec.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("PDF download started");
    } catch (err) {
      toast.error("Download failed");
    } finally {
      setTimeout(() => setDownloading(false), 800);
    }
  };

  return (
    <div className="space-y-8" data-testid="api-docs-container">
      {/* Hero */}
      <Surface className="overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-amber-100/40 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-red-100/40 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 border border-red-200 text-[#9B1C1C] text-xs font-semibold uppercase tracking-widest mb-3">
              <Cable className="h-3 w-3" /> Wings ERP · TharaAPIs
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              Wings × Thara Gold Sync API
            </h1>
            <p className="text-base text-gray-600 mt-3 leading-relaxed">
              Three endpoints under the <span className="font-mono text-[#9B1C1C]">/TharaAPIs/get_data</span> namespace.
              Bearer-token authentication. Pagination with <span className="font-mono">page</span> / <span className="font-mono">page_size</span>{" "}
              (max 50,000) driven by the <span className="font-mono">has_next</span> flag.
              This is the live contract used by the Inventory Suite — the app hits exactly these URLs during Full and Incremental Sync.
            </p>
          </div>

          <div className="flex flex-col gap-3 shrink-0">
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-[#9B1C1C] hover:bg-[#7F1D1D] h-12 px-6 text-base"
              data-testid="api-docs-download-pdf"
            >
              {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><Download className="h-5 w-5 mr-2" /> Download PDF</>)}
            </Button>
            <p className="text-[11px] text-gray-500 text-center">Live-generated PDF spec</p>
          </div>
        </div>
      </Surface>

      {/* Base URL + Auth */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-md p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500">
            <ShieldCheck className="h-3.5 w-3.5" /> Base URL
          </div>
          <p className="font-mono text-sm text-gray-900 mt-2 break-all" data-testid="api-docs-base-url">
            {BASE_URL}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500">
            <KeyRound className="h-3.5 w-3.5" /> Authentication
          </div>
          <p className="font-mono text-sm text-gray-900 mt-2">
            Authorization: Bearer &lt;token&gt;
          </p>
          <p className="text-[11px] text-gray-500 mt-1">Same token for all three endpoints. Rotates via Wings support.</p>
        </div>
      </section>

      {/* The three APIs */}
      <section>
        <div className="mb-4">
          <h2 className="font-heading text-xl font-semibold text-gray-900">The Three Endpoints</h2>
          <p className="text-sm text-gray-500 mt-1">All GET · paginated · same response envelope.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* 1. MasterDetails */}
          <div className="bg-white border border-gray-200 rounded-md p-6 hover:shadow-md transition-all" data-testid="api-endpoint-master">
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-md bg-gradient-to-br from-[#9B1C1C] to-[#7F1D1D] text-white flex items-center justify-center shadow-sm">
                <Database className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-800 px-2 py-0.5 rounded bg-amber-50 border border-amber-200">
                Master Sync
              </span>
            </div>
            <h3 className="font-heading text-lg font-semibold text-gray-900 mt-4">1. MasterDetails</h3>
            <p className="font-mono text-[11px] text-[#B45309] mt-1 break-all">GET /TharaAPIs/get_data/MasterDetails</p>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">
              Product master. Returns catalogue metadata for every SKU — powers the <b>Master Sync</b> button.
            </p>
            <div className="mt-3 rounded bg-gray-50 border border-gray-100 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Query Params</p>
              <p className="font-mono text-[11px] text-gray-800 mt-1">page · page_size (≤ 50000)</p>
            </div>
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Fields</p>
              <p className="font-mono text-[11px] text-gray-700 mt-1 leading-relaxed">
                MasterType · Name · ProductCode · Description · Node · Group · Folder · ActiveStatus · MRPAmount
              </p>
            </div>
          </div>

          {/* 2. StockDetails */}
          <div className="bg-white border border-gray-200 rounded-md p-6 hover:shadow-md transition-all" data-testid="api-endpoint-stock">
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-md bg-gradient-to-br from-[#9B1C1C] to-[#7F1D1D] text-white flex items-center justify-center shadow-sm">
                <Database className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-red-800 px-2 py-0.5 rounded bg-red-50 border border-red-200">
                Full Sync
              </span>
            </div>
            <h3 className="font-heading text-lg font-semibold text-gray-900 mt-4">2. StockDetails</h3>
            <p className="font-mono text-[11px] text-[#B45309] mt-1 break-all">GET /TharaAPIs/get_data/StockDetails</p>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">
              Absolute on-hand stock across all branches / batches / locations. Used by Full Sync to rebuild the local snapshot.
            </p>
            <div className="mt-3 rounded bg-gray-50 border border-gray-100 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Query Params</p>
              <p className="font-mono text-[11px] text-gray-800 mt-1">page · page_size (≤ 50000)</p>
            </div>
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Fields</p>
              <p className="font-mono text-[11px] text-gray-700 mt-1 leading-relaxed">
                Branch · Product · ProductCode · ProductNode · ProductBatch · SKU · Location · STORAGEBIN · QUANTITY · DESCRIPTION · Category · ActiveStatus · MRPAmount
              </p>
            </div>
          </div>

          {/* 3. StockDetailsByDate */}
          <div className="bg-white border border-emerald-200 rounded-md p-6 hover:shadow-md transition-all ring-1 ring-emerald-100" data-testid="api-endpoint-stock-by-date">
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-md bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center shadow-sm">
                <Calendar className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-800 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200">
                Incremental + Fast-Moving
              </span>
            </div>
            <h3 className="font-heading text-lg font-semibold text-gray-900 mt-4">3. StockDetailsByDate</h3>
            <p className="font-mono text-[11px] text-[#B45309] mt-1 break-all">GET /TharaAPIs/get_data/StockDetailsByDate</p>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">
              Per-day movements — receipt (inward) and issue (sales/outward) quantities for a specific transaction date.
              This is the endpoint the Incremental Sync hits every 15 minutes with today&apos;s date.
            </p>
            <div className="mt-3 rounded bg-gray-50 border border-gray-100 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Query Params</p>
              <p className="font-mono text-[11px] text-gray-800 mt-1">
                <span className="text-red-700">txn_date=YYYY-MM-DD</span> · page · page_size (≤ 50000)
              </p>
              <p className="text-[10px] text-gray-500 mt-1 italic">Paginate until <span className="font-mono">has_next=false</span>.</p>
            </div>
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Fields</p>
              <p className="font-mono text-[11px] text-gray-700 mt-1 leading-relaxed">
                Branch · Product · ProductCode · ProductNode · ProductBatch · SKU · Location · STORAGEBIN · ReceiptQuantity · IssueQuantity · DESCRIPTION · Category · ActiveStatus · MRPAmount
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Response envelope */}
      <section>
        <div className="mb-4">
          <h2 className="font-heading text-xl font-semibold text-gray-900">Response Envelope</h2>
          <p className="text-sm text-gray-500 mt-1">All three endpoints return the same paginated shape.</p>
        </div>
        <Surface padded={false}>
          <pre className="p-5 text-xs font-mono text-gray-800 leading-relaxed overflow-x-auto" data-testid="api-docs-response-envelope">
{`{
  "status":    "success",
  "page":      1,
  "page_size": 50000,
  "count":     50000,
  "has_next":  true,
  "data": [ /* endpoint-specific rows */ ]
}`}
          </pre>
        </Surface>
      </section>

      {/* How Thara app uses them */}
      <section>
        <div className="mb-4">
          <h2 className="font-heading text-xl font-semibold text-gray-900">How the Inventory Suite Uses Them</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-amber-200 rounded-md p-5 ring-1 ring-amber-100">
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-700">Master Sync</p>
            <p className="text-sm text-gray-800 mt-2 font-medium">On-demand · run when new products added.</p>
            <ul className="mt-3 space-y-1.5 text-xs text-gray-600 leading-relaxed">
              <li>1. Pulls all pages of <span className="font-mono">MasterDetails?page_size=50000</span></li>
              <li>2. Filters to rows with <span className="font-mono">MasterType=&quot;Products&quot;</span></li>
              <li>3. Upserts by ProductCode; drives <span className="font-mono">is_active</span> from <span className="font-mono">ActiveStatus</span></li>
            </ul>
          </div>
          <div className="bg-white border border-red-200 rounded-md p-5 ring-1 ring-red-100">
            <p className="text-xs font-semibold uppercase tracking-widest text-red-700">Full Sync</p>
            <p className="text-sm text-gray-800 mt-2 font-medium">Rebuilds absolute stock levels.</p>
            <ul className="mt-3 space-y-1.5 text-xs text-gray-600 leading-relaxed">
              <li>1. Pulls all pages of <span className="font-mono">StockDetails?page_size=50000</span></li>
              <li>2. Derives branches + suppliers from stock columns</li>
              <li>3. Rebuilds inventory table matched by ProductCode</li>
            </ul>
          </div>
          <div className="bg-white border border-emerald-200 rounded-md p-5 ring-1 ring-emerald-100">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Incremental Sync</p>
            <p className="text-sm text-gray-800 mt-2 font-medium">Runs every 15 min.</p>
            <ul className="mt-3 space-y-1.5 text-xs text-gray-600 leading-relaxed">
              <li>1. Pulls <span className="font-mono">StockDetailsByDate?txn_date=&lt;today&gt;&amp;page_size=50000</span> and paginates until <span className="font-mono">has_next=false</span></li>
              <li>2. Aggregates <span className="font-mono">ReceiptQuantity − IssueQuantity</span> per (product, branch)</li>
              <li>3. Applies to inventory idempotently: <span className="font-mono">qty = qty_base + net_movement_today</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Sample curl */}
      <section>
        <div className="mb-4">
          <h2 className="font-heading text-xl font-semibold text-gray-900">Sample Requests</h2>
        </div>
        <Surface padded={false}>
          <pre className="p-5 text-xs font-mono text-gray-800 leading-relaxed overflow-x-auto whitespace-pre" data-testid="api-docs-curl">
{`# 1. Product master — Master Sync
curl -H "Authorization: Bearer <token>" \\
  "${BASE_URL}/MasterDetails?page=1&page_size=50000"

# 2. Absolute stock — Full Sync
curl -H "Authorization: Bearer <token>" \\
  "${BASE_URL}/StockDetails?page=1&page_size=50000"

# 3. Today's movements — Incremental Sync (every 15 min)
curl -H "Authorization: Bearer <token>" \\
  "${BASE_URL}/StockDetailsByDate?txn_date=2026-07-13&page=1&page_size=50000"`}
          </pre>
        </Surface>
      </section>
    </div>
  );
}
